import {Discipline, type MapEvent} from '@/apiTypes/event';
import type {PendingBounds} from '@/apiTypes/map';
import {formatLocalDateParam} from '@/utils/displayFormatUtils';
import {usePopupContext} from '@/components/PopupProvider';
import {GroupedEventList} from '@/components/groupedEventList/groupedEventList';
import {MapMarkerPopup} from '@/components/mapMarkerPopup/mapMarkerPopup';
import {MiniMap} from '@/components/miniMap/miniMap';
import type {MarkerHoverPosition} from '@/components/miniMap/miniMap';
import {MobileSearch} from '@/components/mobileSearch/mobileSearch';
import {PaginationGroup} from '@/components/paginationGroup/paginationGroup';
import {
  type SearchFilters,
  SearchFiltersModal,
} from '@/components/searchFiltersModal/searchFiltersModal';
import {SearchHereButton} from '@/components/searchHereButton';
import {Navbar} from '@/components/ui/navbar';
import {PillToggle} from '@/components/ui/pillToggle';
import {Spinner} from '@/components/ui/spinner';
import {useIsMobile} from '@/hooks/useIsMobile';
import {useMobileMapPadding} from '@/hooks/useMobileMapPadding';
import {getEventsByBounds} from '@/queries/getEventsByBounds';
import {boundsFromCenter, DEFAULT_MAP_ZOOM} from '@/utils/geoBounds';
import {getMaxEventDistance} from '@/queries/getMaxEventDistance';
import {useAnalytics} from '@/hooks/useAnalytics';
import {getUserLocationFromIp} from '@/server/getUserLocationFromIp';
import {
  createFileRoute,
  useLoaderData,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import {Search as SearchIcon, SlidersHorizontal, X} from 'lucide-react';
import {type LngLatLike} from 'mapbox-gl';
import {
  type ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {z} from 'zod';
import {MobileSearchBar} from '@/components/mobileSearchBar/mobileSearchBar';
import {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
} from '@/components/ui/inputGroup';

/** Default page size for search results */
const DEFAULT_PAGE_SIZE = 20;

/** Maps pill labels to database eventType values */
const PILL_TO_EVENT_TYPE: Record<string, string | undefined> = {
  All: undefined,
  Races: 'race',
  Rides: 'ride',
};

/** Maps database eventType values back to pill labels */
const EVENT_TYPE_TO_PILL: Record<string, string> = {
  race: 'Races',
  ride: 'Rides',
};

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://wayra.cc';

export const Route = createFileRoute('/search')({
  component: Search,
  validateSearch: z.object({
    page: z.number().int().nonnegative().optional().catch(undefined),
    pageSize: z.number().int().nonnegative().optional().catch(undefined),
    mapView: z.boolean().optional().catch(undefined),
    swLat: z.number().optional(),
    swLng: z.number().optional(),
    neLat: z.number().optional(),
    neLng: z.number().optional(),
    selectedEventId: z.string().optional(),
    q: z.string().optional(),
    eventType: z.enum(['race', 'ride', 'route']).optional(),
    disciplines: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    minDistance: z.number().optional(),
    maxDistance: z.number().optional(),
  }),
  loaderDeps: ({
    search: {
      page,
      pageSize,
      swLat,
      swLng,
      neLat,
      neLng,
      q,
      eventType,
      disciplines,
      dateFrom,
      dateTo,
      minDistance,
      maxDistance,
    },
  }) => ({
    page,
    pageSize,
    swLat,
    swLng,
    neLat,
    neLng,
    q,
    eventType,
    disciplines,
    dateFrom,
    dateTo,
    minDistance,
    maxDistance,
  }),
  head: () => ({
    links: [{rel: 'canonical', href: `${SITE_URL}/search`}],
  }),
  loader: async ({deps: search}) => {
    const {
      page = 1,
      pageSize = DEFAULT_PAGE_SIZE,
      swLat,
      swLng,
      neLat,
      neLng,
      q,
      eventType,
      disciplines,
      dateFrom,
      dateTo,
      minDistance,
      maxDistance,
    } = search || {};
    const userLocation = await getUserLocationFromIp();

    const parsedDisciplines = disciplines
      ? disciplines
          .split(',')
          .filter(Boolean)
          .filter((d): d is Discipline => Discipline.safeParse(d).success)
      : undefined;

    // Fetch the max route distance in parallel with the main query
    const maxEventDistancePromise = getMaxEventDistance();

    // Use explicit bounds if provided, otherwise compute from user location
    const hasExplicitBounds =
      typeof swLat === 'number' &&
      typeof swLng === 'number' &&
      typeof neLat === 'number' &&
      typeof neLng === 'number';

    let southWest: {lat: number; lng: number};
    let northEast: {lat: number; lng: number};

    // Fall back to NYC center when IP geolocation returns 0/0
    const hasUserLocation = userLocation.lat !== 0 || userLocation.lng !== 0;
    const resolvedLocation = hasUserLocation
      ? userLocation
      : {lat: 40.7128, lng: -73.906};

    if (hasExplicitBounds) {
      southWest = {lat: swLat, lng: swLng};
      northEast = {lat: neLat, lng: neLng};
    } else {
      const defaultBounds = boundsFromCenter(
        resolvedLocation,
        DEFAULT_MAP_ZOOM
      );
      southWest = {lat: defaultBounds.swLat, lng: defaultBounds.swLng};
      northEast = {lat: defaultBounds.neLat, lng: defaultBounds.neLng};
    }

    const [boundsResult, {maxDistance: maxEventDistance}] = await Promise.all([
      getEventsByBounds({
        data: {
          southWest,
          northEast,
          page,
          pageSize,
          q,
          eventType,
          disciplines: parsedDisciplines,
          dateFrom,
          dateTo,
          minDistance,
          maxDistance,
        },
      }),
      maxEventDistancePromise,
    ]);

    return {
      events: {
        events: boundsResult.events,
        pagination: boundsResult.pagination,
      },
      ghostEvents: boundsResult.ghostEvents,
      userLocation: resolvedLocation,
      maxEventDistance,
    };
  },
});

function Search() {
  const {
    events: {events, pagination},
    ghostEvents,
    userLocation,
    maxEventDistance,
  } = useLoaderData({from: '/search'});
  const navigate = useNavigate();
  const routerState = useRouterState();
  const {recordEventView, fireBehavioralPopup} = usePopupContext();
  /** Tracks event IDs already counted this session to prevent double-counting hover + click */
  const viewedEventIds = useRef(new Set<string>());
  const recordUniqueEventView = useCallback(
    (eventId: string) => {
      if (viewedEventIds.current.has(eventId)) return;
      viewedEventIds.current.add(eventId);
      recordEventView();
    },
    [recordEventView]
  );
  const isLoading = routerState.isLoading;
  const {
    mapView,
    selectedEventId: urlSelectedEventId,
    q: urlQuery,
    eventType: urlEventType,
    disciplines: urlDisciplines,
    dateFrom: urlDateFrom,
    dateTo: urlDateTo,
    minDistance: urlMinDistance,
    maxDistance: urlMaxDistance,
  } = routerState.location.search;

  const {track} = useAnalytics();
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    track('Search Performed', {
      q: urlQuery,
      event_type: urlEventType,
      disciplines: urlDisciplines,
      date_from: urlDateFrom,
      date_to: urlDateTo,
      min_distance: urlMinDistance,
      max_distance: urlMaxDistance,
    });
  }, [
    urlQuery,
    urlEventType,
    urlDisciplines,
    urlDateFrom,
    urlDateTo,
    urlMinDistance,
    urlMaxDistance,
  ]);

  // Local input buffer — holds what the user is typing before committing with Enter
  const [searchText, setSearchText] = useState(urlQuery ?? '');

  const [highlightedEventId, setHighlightedEventId] = useState<string | null>(
    null
  );
  const [markerHoverPosition, setMarkerHoverPosition] =
    useState<MarkerHoverPosition | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Pending map bounds that haven't been committed as a search yet
  const [pendingBounds, setPendingBounds] = useState<PendingBounds | null>(
    null
  );

  // Whether the "Search Here" button should be shown
  const pendingAreaSearch = pendingBounds !== null;

  // Counter to tell MiniMap to re-fit bounds after a text search completes
  const [fitToMarkersKey, setFitToMarkersKey] = useState(0);
  const prevQueryRef = useRef(urlQuery);

  // Keep the local input buffer in sync when the URL query changes externally
  // (e.g. browser back/forward navigation)
  useEffect(() => {
    setSearchText(urlQuery ?? '');
  }, [urlQuery]);

  // Fit the map to all result markers after a text search completes
  useEffect(() => {
    if (prevQueryRef.current !== urlQuery && !isLoading) {
      prevQueryRef.current = urlQuery;
      setFitToMarkersKey((k) => k + 1);
    }
  }, [urlQuery, isLoading]);

  // Filter modal state
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Whether the mobile search overlay (typeahead) is active
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);

  /** Current filters derived from URL params */
  const currentFilters: SearchFilters = useMemo(
    () => ({
      eventType: urlEventType,
      disciplines: urlDisciplines
        ? urlDisciplines
            .split(',')
            .filter(Boolean)
            .filter((d): d is Discipline => Discipline.safeParse(d).success)
        : [],
      dateFrom: urlDateFrom ? new Date(urlDateFrom) : undefined,
      dateTo: urlDateTo ? new Date(urlDateTo) : undefined,
      minDistance: urlMinDistance,
      maxDistance: urlMaxDistance,
    }),
    [
      urlEventType,
      urlDisciplines,
      urlDateFrom,
      urlDateTo,
      urlMinDistance,
      urlMaxDistance,
    ]
  );

  /** Count of active filters for the badge */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (currentFilters.eventType) count++;
    if (currentFilters.disciplines.length > 0) count++;
    if (currentFilters.dateFrom) count++;
    if (currentFilters.minDistance || currentFilters.maxDistance) count++;
    return count;
  }, [currentFilters]);

  /** Apply filters from the modal to URL params */
  const handleFiltersApply = useCallback(
    (filters: SearchFilters) => {
      const disciplinesParam =
        filters.disciplines.length > 0
          ? filters.disciplines.join(',')
          : undefined;
      const dateFromParam = filters.dateFrom
        ? formatLocalDateParam(filters.dateFrom)
        : undefined;
      const dateToParam = filters.dateTo
        ? formatLocalDateParam(filters.dateTo)
        : undefined;

      navigate({
        to: '/search',
        search: (prev) => ({
          ...prev,
          page: 1,
          eventType: filters.eventType,
          disciplines: disciplinesParam,
          dateFrom: dateFromParam,
          dateTo: dateToParam,
          minDistance: filters.minDistance,
          maxDistance: filters.maxDistance,
        }),
      });
    },
    [navigate]
  );

  /** Update the local search text input buffer */
  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchText(e.target.value);
  };

  /** Submit the text search by updating the `q` URL param, clearing map bounds for a global search */
  const handleTextSearch = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      navigate({
        to: '/search',
        search: (prev) => ({
          ...prev,
          page: 1,
          q: trimmed || undefined,
          swLat: undefined,
          swLng: undefined,
          neLat: undefined,
          neLng: undefined,
        }),
      });
      setPendingBounds(null);
    },
    [navigate]
  );

  /** Clear the text search and re-search within the current map viewport */
  const handleClearSearch = useCallback(
    (bounds: PendingBounds | null) => {
      setSearchText('');
      if (bounds) {
        navigate({
          to: '/search',
          search: (prev) => ({
            ...prev,
            page: 1,
            q: undefined,
            swLat: bounds.swLat,
            swLng: bounds.swLng,
            neLat: bounds.neLat,
            neLng: bounds.neLng,
          }),
        });
      } else {
        navigate({
          to: '/search',
          search: (prev) => ({
            ...prev,
            page: 1,
            q: undefined,
          }),
        });
      }
    },
    [navigate]
  );

  /** Handle marker click — desktop navigates to event page, mobile selects event via state */
  const handleMarkerClick = useCallback(
    (id: string) => {
      const isDesktop = window.innerWidth >= 1024;
      if (isDesktop) {
        recordUniqueEventView(id);
        navigate({to: `/event/${id}`});
      } else {
        recordUniqueEventView(id);
        setHighlightedEventId(id);
      }
    },
    [navigate, recordUniqueEventView]
  );

  /** Handle desktop marker hover with position data for the floating popup */
  const handleMarkerHoverWithPosition = useCallback(
    (position: MarkerHoverPosition | null) => {
      // On mobile, iOS synthesizes mouseenter events after swipe gestures,
      // which would re-highlight a just-deselected marker. Hover state is
      // desktop-only — taps handle selection on mobile.
      if (position && window.innerWidth < 1024) return;
      setMarkerHoverPosition(position);
      if (position) recordUniqueEventView(position.id);
      // Don't clear the highlight from hover events on mobile
      if (!position && window.innerWidth < 1024) return;
      setHighlightedEventId(position?.id ?? null);
    },
    [recordUniqueEventView]
  );

  /** The event currently being hovered on the map, used for the popup */
  const hoveredEvent = useMemo((): MapEvent | null => {
    if (!markerHoverPosition) return null;
    const id = markerHoverPosition.id;
    return (
      events.find((e) => e.id === id) ??
      ghostEvents.find((e) => e.id === id) ??
      null
    );
  }, [events, ghostEvents, markerHoverPosition]);

  const handlePaginationChange = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  const markers = useMemo(
    () =>
      events
        .filter((e) => typeof e.lng === 'number' && typeof e.lat === 'number')
        .map((e) => ({
          lat: e.lat as number,
          lng: e.lng as number,
          id: e.id,
          eventType: e.eventType,
          startDate: e.startDate,
        })),
    [events]
  );

  const mapOptions = useMemo(() => ({center: userLocation}), [userLocation]);
  const fitBoundsPadding = useMobileMapPadding();
  const isMobile = useIsMobile();

  /** Navigate to a specific event's detail page (used by mobile event card) */
  const handleMobileEventCardClick = useCallback(
    (eventId: string) => {
      recordUniqueEventView(eventId);
      navigate({to: `/event/${eventId}`});
    },
    [navigate, recordUniqueEventView]
  );

  /** Store pending bounds when user moves the map (don't auto-fetch) */
  const handleMapMove = useCallback(
    (southWest: LngLatLike, northEast: LngLatLike) => {
      const extractCoords = (coord: LngLatLike): {lng: number; lat: number} => {
        if (Array.isArray(coord)) {
          return {lng: coord[0], lat: coord[1]};
        }
        if ('lng' in coord) {
          return {lng: coord.lng, lat: coord.lat};
        }
        if ('lon' in coord) {
          return {lng: coord.lon, lat: coord.lat};
        }
        return {
          lng: (coord as {lng: number; lat: number}).lng,
          lat: (coord as {lng: number; lat: number}).lat,
        };
      };

      const swCoords = extractCoords(southWest);
      const neCoords = extractCoords(northEast);

      if (
        typeof swCoords.lat !== 'number' ||
        typeof swCoords.lng !== 'number' ||
        typeof neCoords.lat !== 'number' ||
        typeof neCoords.lng !== 'number' ||
        Number.isNaN(swCoords.lat) ||
        Number.isNaN(swCoords.lng) ||
        Number.isNaN(neCoords.lat) ||
        Number.isNaN(neCoords.lng)
      ) {
        console.warn('Invalid coordinates received in handleMapMove:', {
          swCoords,
          neCoords,
        });
        return;
      }

      setPendingBounds({
        swLat: swCoords.lat,
        swLng: swCoords.lng,
        neLat: neCoords.lat,
        neLng: neCoords.lng,
      });
    },
    []
  );

  /** Update the eventType URL param when a filter pill is selected */
  const handleFilterChange = useCallback(
    (pill: string) => {
      const newEventType = PILL_TO_EVENT_TYPE[pill];
      navigate({
        to: '/search',
        search: (prev) => ({
          ...prev,
          page: 1,
          eventType: newEventType,
        }),
      });
    },
    [navigate]
  );

  /** The active pill label derived from the URL eventType param */
  const activePill = urlEventType
    ? (EVENT_TYPE_TO_PILL[urlEventType] ?? 'All')
    : 'All';

  /** Navigate with new bounds from the "Search This Area" button */
  const handleSearchThisArea = useCallback(() => {
    if (!pendingBounds) return;

    navigate({
      to: '/search',
      search: (prev) => ({
        ...prev,
        page: 1,
        swLat: pendingBounds.swLat,
        swLng: pendingBounds.swLng,
        neLat: pendingBounds.neLat,
        neLng: pendingBounds.neLng,
      }),
    });
    setPendingBounds(null);
  }, [navigate, pendingBounds]);

  return (
    <div className="h-screen flex flex-col">
      <Navbar className="hidden lg:block">
        <InputGroup className="relative w-full max-w-md">
          <InputGroupAddon>
            <SearchIcon className="text-slate-400 pointer-events-none" />
          </InputGroupAddon>
          <InputGroupInput
            type="search"
            enterKeyHint="search"
            placeholder="Search events..."
            value={searchText}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleTextSearch(searchText);
                e.currentTarget.blur();
              }
            }}
            className="pl-9 pr-9 h-9 rounded-lg"
          />
          {searchText && (
            <button
              onClick={() => handleClearSearch(pendingBounds)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </InputGroup>
      </Navbar>
      <main className="flex flex-1 overflow-hidden relative">
        <div
          className={`w-full lg:w-1/3 flex flex-col border-r bg-muted absolute lg:relative transition-transform duration-300 z-20 h-full translate-y-full lg:translate-y-0`}
        >
          <div className="flex p-4 border-b justify-center items-center gap-3">
            <PillToggle
              highlightColor="bg-primary"
              value={activePill}
              options={['All', 'Races', 'Rides']}
              onChange={handleFilterChange}
            />
            <button
              onClick={() => setFiltersOpen(true)}
              className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-sm font-medium text-slate-700 hover:border-slate-900 transition-colors cursor-pointer bg-white"
            >
              <SlidersHorizontal className="size-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="flex items-center justify-center size-5 rounded-full bg-slate-900 text-white text-xs font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Spinner size="lg" />
              </div>
            ) : events.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <SearchIcon className="h-10 w-10 text-slate-300 mb-3" />
                <p className="text-sm font-medium text-slate-700">
                  No events found
                </p>
              </div>
            ) : (
              <GroupedEventList
                events={events}
                onEventMouseEnter={(id) => {
                  setHighlightedEventId(id);
                  recordUniqueEventView(id);
                }}
                onEventMouseLeave={() => setHighlightedEventId(null)}
              />
            )}
          </div>
          <div className="p-4 border-t">
            <PaginationGroup
              pagination={pagination}
              onPageChange={handlePaginationChange}
              isLoading={isLoading}
            />
          </div>
        </div>
        <div className="flex-1 bg-gray-50 lg:block relative">
          <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none lg:hidden" />
          <MiniMap
            onMarkerClick={handleMarkerClick}
            onMapMove={handleMapMove}
            onMarkerHoverWithPosition={handleMarkerHoverWithPosition}
            onEmptyMapClick={() => {
              if (!highlightedEventId) return;
              setHighlightedEventId(null);
              fireBehavioralPopup();
            }}
            highlightMarkerId={highlightedEventId}
            centerOnMarkerId={isMobile ? highlightedEventId : null}
            options={mapOptions}
            markers={markers}
            fitToMarkersKey={fitToMarkersKey}
            fitBoundsPadding={fitBoundsPadding}
            ghostEvents={ghostEvents}
          />

          {markerHoverPosition && hoveredEvent && (
            <div className="hidden lg:block">
              <MapMarkerPopup
                event={hoveredEvent}
                x={markerHoverPosition.x}
                y={markerHoverPosition.y}
                markerHeight={markerHoverPosition.markerHeight}
                mapBounds={markerHoverPosition.mapBounds}
              />
            </div>
          )}

          {pendingAreaSearch && (
            <SearchHereButton
              className="hidden lg:block absolute top-4 left-1/2 -translate-x-1/2 z-10"
              onClick={handleSearchThisArea}
              isLoading={isLoading}
            />
          )}
        </div>

        {isMobile && (
          <>
            <MobileSearch
              events={events}
              ghostEvents={ghostEvents}
              isLoading={isLoading}
              urlQuery={urlQuery}
              pendingAreaSearch={pendingAreaSearch}
              pendingBounds={pendingBounds}
              highlightedEventId={highlightedEventId}
              hidden={mobileSearchVisible}
              onSearchThisArea={handleSearchThisArea}
              onHighlightEvent={setHighlightedEventId}
            />
            <MobileSearchBar
              onFilterClick={() => setFiltersOpen(true)}
              activeFilterCount={activeFilterCount}
              onSearchChange={setSearchText}
              onSearchSubmit={handleTextSearch}
              onSearchVisibleChange={setMobileSearchVisible}
              searchText={searchText}
              onEventClick={handleMobileEventCardClick}
            />
          </>
        )}
      </main>

      <SearchFiltersModal
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={currentFilters}
        onApply={handleFiltersApply}
        maxDistanceLimit={maxEventDistance}
      />
    </div>
  );
}
